import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Bell,
	Check,
	CheckCheck,
	Trash2,
	Trash,
	Filter,
	RefreshCw,
	ChevronDown,
	AlertCircle,
	Clock,
	Briefcase,
	ClipboardList,
	MessageSquare,
	ShieldCheck,
} from 'lucide-react'
import { apiClient } from '@api'
import { useNotificationStore } from '@store'
import { formatRelativeTime } from '@utils'
import { Button } from '@components/common'
import './Notifications.scss'

// ─── Constants ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 20

const TYPE_GROUPS = [
	{ value: '', label: 'All Types' },
	{ value: 'tasks', label: 'Tasks', types: ['task_assigned', 'task_started', 'task_submitted', 'task_approved', 'task_rejected', 'task_completed', 'task_due_soon', 'task_overdue'] },
	{ value: 'projects', label: 'Projects', types: ['project_created', 'project_submitted', 'pm_review_complete', 'executive_review_needed', 'project_activated', 'project_status_changed', 'project_approved'] },
	{ value: 'checklists', label: 'Checklists', types: ['checklist_pending_pm_review', 'checklist_approved_by_pm', 'checklist_rejected_by_pm', 'checklist_approved_by_management', 'checklist_rejected_by_management', 'checklist_revoked'] },
	{ value: 'comments', label: 'Comments', types: ['comment', 'comment_added'] },
	{ value: 'approvals', label: 'Approvals', types: ['approval_needed'] },
]

const PRIORITY_OPTIONS = [
	{ value: '', label: 'All Priorities' },
	{ value: 'urgent', label: 'Urgent' },
	{ value: 'high', label: 'High' },
	{ value: 'normal', label: 'Normal' },
	{ value: 'low', label: 'Low' },
]

// ─── Type icon + colour helpers ───────────────────────────────────────────────

const TYPE_META = {
	// Tasks
	task_assigned:   { emoji: '📋', group: 'tasks',     colour: '#3b82f6' },
	task_started:    { emoji: '▶️',  group: 'tasks',     colour: '#3b82f6' },
	task_submitted:  { emoji: '📤', group: 'tasks',     colour: '#f59e0b' },
	task_approved:   { emoji: '✅', group: 'tasks',     colour: '#10b981' },
	task_rejected:   { emoji: '❌', group: 'tasks',     colour: '#ef4444' },
	task_completed:  { emoji: '🎉', group: 'tasks',     colour: '#10b981' },
	task_due_soon:   { emoji: '⏰', group: 'tasks',     colour: '#f59e0b' },
	task_overdue:    { emoji: '🚨', group: 'tasks',     colour: '#ef4444' },
	// Projects
	project_created:          { emoji: '🗂️', group: 'projects', colour: '#6366f1' },
	project_submitted:        { emoji: '📨', group: 'projects', colour: '#f59e0b' },
	pm_review_complete:       { emoji: '✔️', group: 'projects', colour: '#10b981' },
	executive_review_needed:  { emoji: '👔', group: 'projects', colour: '#f59e0b' },
	project_activated:        { emoji: '🚀', group: 'projects', colour: '#10b981' },
	project_status_changed:   { emoji: '🔄', group: 'projects', colour: '#6366f1' },
	project_approved:         { emoji: '🎉', group: 'projects', colour: '#10b981' },
	// Checklists
	checklist_pending_pm_review:       { emoji: '📝', group: 'checklists', colour: '#f59e0b' },
	checklist_approved_by_pm:          { emoji: '✅', group: 'checklists', colour: '#10b981' },
	checklist_rejected_by_pm:          { emoji: '↩️', group: 'checklists', colour: '#f59e0b' },
	checklist_approved_by_management:  { emoji: '✅', group: 'checklists', colour: '#10b981' },
	checklist_rejected_by_management:  { emoji: '❌', group: 'checklists', colour: '#ef4444' },
	checklist_revoked:                 { emoji: '🔙', group: 'checklists', colour: '#ef4444' },
	// Comments + approvals
	comment:          { emoji: '💬', group: 'comments',  colour: '#6366f1' },
	comment_added:    { emoji: '💬', group: 'comments',  colour: '#6366f1' },
	approval_needed:  { emoji: '✅', group: 'approvals', colour: '#f59e0b' },
}

const getTypeMeta = (type) => TYPE_META[type] || { emoji: '🔔', group: 'other', colour: '#64748b' }

const getPriorityConfig = (priority) => {
	switch (priority) {
		case 'urgent': return { label: 'Urgent', cls: 'priority--urgent' }
		case 'high':   return { label: 'High',   cls: 'priority--high'   }
		case 'low':    return { label: 'Low',     cls: 'priority--low'    }
		default:       return null
	}
}

// ─── Component ────────────────────────────────────────────────────────────────

const Notifications = () => {
	const navigate = useNavigate()
	const { markAsRead, markAllAsRead, deleteNotification: removeFromStore, unreadCount } = useNotificationStore()

	// Local fetch state (independent of dropdown store slice)
	const [items, setItems] = useState([])
	const [pagination, setPagination] = useState({ total: 0, has_more: false, offset: 0 })
	const [loading, setLoading] = useState(false)
	const [loadingMore, setLoadingMore] = useState(false)
	const [error, setError] = useState(null)

	// Filters
	const [tab, setTab] = useState('all')          // all | unread | read
	const [typeGroup, setTypeGroup] = useState('')  // '' | tasks | projects | checklists | comments | approvals
	const [priority, setPriority] = useState('')    // '' | urgent | high | normal | low
	const [showFilters, setShowFilters] = useState(false)

	// Selection (for bulk delete)
	const [selected, setSelected] = useState(new Set())

	// Deleting state
	const [deletingIds, setDeletingIds] = useState(new Set())
	const [clearingRead, setClearingRead] = useState(false)

	// Keep latest filters in a ref so callbacks don't go stale
	const filterRef = useRef({ tab, typeGroup, priority })
	useEffect(() => { filterRef.current = { tab, typeGroup, priority } }, [tab, typeGroup, priority])

	// ── Fetch ───────────────────────────────────────────────────────────────

	const buildParams = useCallback((offset = 0, overrides = {}) => {
		const { tab: t, typeGroup: tg, priority: p } = { ...filterRef.current, ...overrides }
		const params = new URLSearchParams()
		params.set('limit', PAGE_SIZE)
		params.set('offset', offset)
		if (t === 'unread') params.set('unread', 'true')
		if (t === 'read')   params.set('read', 'true')
		if (p) params.set('priority', p)
		// type-group filtering happens client-side (backend only supports single type)
		return params.toString()
	}, [])

	const applyTypeFilter = useCallback((notifs, tg) => {
		if (!tg) return notifs
		const group = TYPE_GROUPS.find(g => g.value === tg)
		if (!group || !group.types) return notifs
		return notifs.filter(n => group.types.includes(n.type))
	}, [])

	const fetchPage = useCallback(async (offset = 0, append = false) => {
		if (append) setLoadingMore(true)
		else         setLoading(true)
		setError(null)

		try {
			const qs = buildParams(offset)
			const res = await apiClient.get(`/notifications?${qs}`)
			const raw = res.data || []
			const filtered = applyTypeFilter(raw, filterRef.current.typeGroup)
			const paginationData = res.pagination || {}

			// For read-only tab filter client-side on top of backend
			const finalItems = filterRef.current.tab === 'read'
				? filtered.filter(n => n.read_at || n.is_read)
				: filtered

			setItems(prev => append ? [...prev, ...finalItems] : finalItems)
			setPagination({
				total: paginationData.total || 0,
				has_more: paginationData.has_more || false,
				offset: offset + PAGE_SIZE,
			})
		} catch (err) {
			setError('Failed to load notifications. Please try again.')
		} finally {
			setLoading(false)
			setLoadingMore(false)
		}
	}, [buildParams, applyTypeFilter])

	// Refetch when filters change
	useEffect(() => {
		setSelected(new Set())
		fetchPage(0, false)
	}, [tab, typeGroup, priority]) // eslint-disable-line react-hooks/exhaustive-deps

	// ── Actions ──────────────────────────────────────────────────────────────

	const handleMarkAsRead = useCallback(async (id) => {
		await markAsRead(id)
		setItems(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString(), is_read: true } : n))
	}, [markAsRead])

	const handleMarkAllRead = useCallback(async () => {
		await markAllAsRead()
		setItems(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString(), is_read: true })))
	}, [markAllAsRead])

	const handleDelete = useCallback(async (id, wasUnread) => {
		setDeletingIds(prev => new Set([...prev, id]))
		try {
			await apiClient.delete(`/notifications/${id}`)
			removeFromStore(id, wasUnread)
			setItems(prev => prev.filter(n => n.id !== id))
			setSelected(prev => { const s = new Set(prev); s.delete(id); return s })
		} catch {
			// silent – item stays in list
		} finally {
			setDeletingIds(prev => { const s = new Set(prev); s.delete(id); return s })
		}
	}, [removeFromStore])

	const handleDeleteSelected = useCallback(async () => {
		const ids = [...selected]
		await Promise.all(ids.map(id => {
			const n = items.find(x => x.id === id)
			return handleDelete(id, n ? !n.read_at : false)
		}))
		setSelected(new Set())
	}, [selected, items, handleDelete])

	const handleClearRead = useCallback(async () => {
		setClearingRead(true)
		try {
			await apiClient.delete('/notifications/clear-read')
			setItems(prev => prev.filter(n => !n.read_at && !n.is_read))
		} catch {
			// silent
		} finally {
			setClearingRead(false)
		}
	}, [])

	const handleNotificationClick = useCallback((notification) => {
		if (!notification.read_at && !notification.is_read) {
			handleMarkAsRead(notification.id)
		}
		if (notification.link) {
			navigate(notification.link)
		}
	}, [navigate, handleMarkAsRead])

	// ── Selection helpers ────────────────────────────────────────────────────

	const toggleSelect = (id) => {
		setSelected(prev => {
			const s = new Set(prev)
			s.has(id) ? s.delete(id) : s.add(id)
			return s
		})
	}

	const toggleSelectAll = () => {
		if (selected.size === items.length) {
			setSelected(new Set())
		} else {
			setSelected(new Set(items.map(n => n.id)))
		}
	}

	// ── Derived ──────────────────────────────────────────────────────────────

	const hasFilters = typeGroup !== '' || priority !== ''
	const readCount  = items.filter(n => n.read_at || n.is_read).length
	const unreadLocal = items.filter(n => !n.read_at && !n.is_read).length

	// ── Render ───────────────────────────────────────────────────────────────

	const renderItem = (notification) => {
		const { id, type, title, message, link, priority: prio, read_at, is_read, created_at } = notification
		const isUnread = !read_at && !is_read
		const meta  = getTypeMeta(type)
		const prioCfg = getPriorityConfig(prio)
		const isDeleting = deletingIds.has(id)
		const isSelected = selected.has(id)

		return (
			<div
				key={id}
				className={[
					'notif-item',
					isUnread   ? 'notif-item--unread'   : '',
					isSelected ? 'notif-item--selected'  : '',
					isDeleting ? 'notif-item--deleting'  : '',
					prio === 'urgent' ? 'notif-item--urgent' : '',
					prio === 'high'   ? 'notif-item--high'   : '',
				].filter(Boolean).join(' ')}
			>
				{/* Checkbox */}
				<label className="notif-item__check" onClick={e => e.stopPropagation()}>
					<input
						type="checkbox"
						checked={isSelected}
						onChange={() => toggleSelect(id)}
					/>
				</label>

				{/* Clickable body */}
				<div
					className="notif-item__body"
					onClick={() => handleNotificationClick(notification)}
					role="button"
					tabIndex={0}
					onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleNotificationClick(notification)}
				>
					<div className="notif-item__icon" style={{ '--icon-colour': meta.colour }}>
						{meta.emoji}
					</div>

					<div className="notif-item__content">
						<div className="notif-item__row">
							<span className="notif-item__title">{title}</span>
							{isUnread && <span className="notif-item__dot" aria-label="Unread" />}
							{prioCfg && (
								<span className={`notif-item__priority ${prioCfg.cls}`}>{prioCfg.label}</span>
							)}
						</div>
						{message && <p className="notif-item__message">{message}</p>}
						<div className="notif-item__meta">
							<span className="notif-item__time">
								<Clock size={11} />
								{formatRelativeTime(created_at)}
							</span>
							{link && <span className="notif-item__link-hint">Click to open →</span>}
						</div>
					</div>
				</div>

				{/* Actions */}
				<div className="notif-item__actions">
					{isUnread && (
						<button
							className="notif-item__action-btn"
							title="Mark as read"
							onClick={e => { e.stopPropagation(); handleMarkAsRead(id) }}
						>
							<Check size={14} />
						</button>
					)}
					<button
						className="notif-item__action-btn notif-item__action-btn--danger"
						title="Delete"
						disabled={isDeleting}
						onClick={e => { e.stopPropagation(); handleDelete(id, isUnread) }}
					>
						<Trash2 size={14} />
					</button>
				</div>
			</div>
		)
	}

	return (
		<div className="notifications-page">
			{/* ── Page Header ─────────────────────────────────────────────── */}
			<div className="notifications-page__header">
				<div className="notifications-page__header-left">
					<div className="notifications-page__icon">
						<Bell size={22} />
					</div>
					<div>
						<h1 className="notifications-page__title">Notifications</h1>
						<p className="notifications-page__subtitle">
							{unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'All caught up'}
						</p>
					</div>
				</div>

				<div className="notifications-page__header-actions">
					<button
						className="notif-action-btn"
						onClick={() => fetchPage(0)}
						title="Refresh"
						disabled={loading}
					>
						<RefreshCw size={16} className={loading ? 'spin' : ''} />
						Refresh
					</button>
					{unreadCount > 0 && (
						<button className="notif-action-btn" onClick={handleMarkAllRead}>
							<CheckCheck size={16} />
							Mark all read
						</button>
					)}
					{readCount > 0 && (
						<button
							className="notif-action-btn notif-action-btn--danger"
							onClick={handleClearRead}
							disabled={clearingRead}
						>
							<Trash size={16} />
							{clearingRead ? 'Clearing…' : 'Clear read'}
						</button>
					)}
				</div>
			</div>

			{/* ── Tabs + Filter bar ────────────────────────────────────────── */}
			<div className="notifications-page__controls">
				{/* Tabs */}
				<div className="notif-tabs">
					{[
						{ key: 'all',    label: 'All' },
						{ key: 'unread', label: 'Unread', count: unreadCount },
						{ key: 'read',   label: 'Read' },
					].map(({ key, label, count }) => (
						<button
							key={key}
							className={`notif-tab ${tab === key ? 'notif-tab--active' : ''}`}
							onClick={() => setTab(key)}
						>
							{label}
							{count != null && count > 0 && (
								<span className="notif-tab__count">{count > 99 ? '99+' : count}</span>
							)}
						</button>
					))}
				</div>

				{/* Filter toggle */}
				<button
					className={`notif-filter-toggle ${hasFilters ? 'notif-filter-toggle--active' : ''}`}
					onClick={() => setShowFilters(v => !v)}
				>
					<Filter size={14} />
					Filters
					{hasFilters && <span className="notif-filter-toggle__dot" />}
					<ChevronDown size={13} className={showFilters ? 'rotate-180' : ''} />
				</button>
			</div>

			{/* ── Expandable filter panel ──────────────────────────────────── */}
			{showFilters && (
				<div className="notif-filter-panel">
					<div className="notif-filter-panel__group">
						<label>Type</label>
						<div className="notif-filter-chips">
							{TYPE_GROUPS.map(g => (
								<button
									key={g.value}
									className={`notif-chip ${typeGroup === g.value ? 'notif-chip--active' : ''}`}
									onClick={() => setTypeGroup(g.value)}
								>
									{g.label === 'Tasks'      && <ClipboardList size={12} />}
									{g.label === 'Projects'   && <Briefcase size={12} />}
									{g.label === 'Checklists' && <ShieldCheck size={12} />}
									{g.label === 'Comments'   && <MessageSquare size={12} />}
									{g.label === 'Approvals'  && <AlertCircle size={12} />}
									{g.label}
								</button>
							))}
						</div>
					</div>

					<div className="notif-filter-panel__group">
						<label>Priority</label>
						<div className="notif-filter-chips">
							{PRIORITY_OPTIONS.map(p => (
								<button
									key={p.value}
									className={`notif-chip ${priority === p.value ? 'notif-chip--active' : ''} ${p.value ? `notif-chip--${p.value}` : ''}`}
									onClick={() => setPriority(p.value)}
								>
									{p.label}
								</button>
							))}
						</div>
					</div>

					{hasFilters && (
						<button
							className="notif-chip notif-chip--clear"
							onClick={() => { setTypeGroup(''); setPriority('') }}
						>
							✕ Clear filters
						</button>
					)}
				</div>
			)}

			{/* ── Bulk actions bar ─────────────────────────────────────────── */}
			{selected.size > 0 && (
				<div className="notif-bulk-bar">
					<span>{selected.size} selected</span>
					<button className="notif-bulk-btn notif-bulk-btn--danger" onClick={handleDeleteSelected}>
						<Trash2 size={14} />
						Delete selected
					</button>
					<button className="notif-bulk-btn" onClick={() => setSelected(new Set())}>
						Cancel
					</button>
				</div>
			)}

			{/* ── List ─────────────────────────────────────────────────────── */}
			<div className="notifications-page__list-wrap">
				{/* Select all bar (only when items present) */}
				{items.length > 0 && !loading && (
					<div className="notif-select-bar">
						<label>
							<input
								type="checkbox"
								checked={selected.size === items.length && items.length > 0}
								onChange={toggleSelectAll}
							/>
							Select all
						</label>
						<span className="notif-select-bar__count">
							{pagination.total} notification{pagination.total !== 1 ? 's' : ''}
						</span>
					</div>
				)}

				{/* Loading skeleton */}
				{loading && (
					<div className="notif-list">
						{Array.from({ length: 6 }).map((_, i) => (
							<div key={i} className="notif-skeleton">
								<div className="notif-skeleton__icon" />
								<div className="notif-skeleton__lines">
									<div className="notif-skeleton__line notif-skeleton__line--title" />
									<div className="notif-skeleton__line notif-skeleton__line--msg" />
									<div className="notif-skeleton__line notif-skeleton__line--meta" />
								</div>
							</div>
						))}
					</div>
				)}

				{/* Error */}
				{!loading && error && (
					<div className="notif-error">
						<AlertCircle size={20} />
						<span>{error}</span>
						<button onClick={() => fetchPage(0)}>Try again</button>
					</div>
				)}

				{/* Empty state */}
				{!loading && !error && items.length === 0 && (
					<div className="notif-empty">
						<div className="notif-empty__icon">🔔</div>
						<h3>No notifications</h3>
						<p>
							{tab === 'unread' ? "You're all caught up — no unread notifications." :
							 tab === 'read'   ? 'No read notifications found.' :
							 hasFilters       ? 'No notifications match the current filters.' :
							                    "You'll see task updates, approvals, and project activity here."}
						</p>
						{hasFilters && (
							<button
								className="notif-chip notif-chip--clear"
								style={{ margin: '0 auto' }}
								onClick={() => { setTypeGroup(''); setPriority(''); setTab('all') }}
							>
								Clear filters
							</button>
						)}
					</div>
				)}

				{/* Items */}
				{!loading && !error && items.length > 0 && (
					<div className="notif-list">
						{items.map(renderItem)}
					</div>
				)}

				{/* Load more */}
				{!loading && !error && pagination.has_more && (
					<div className="notif-load-more">
						<Button
							variant="outline"
							size="small"
							onClick={() => fetchPage(pagination.offset, true)}
							disabled={loadingMore}
						>
							{loadingMore ? 'Loading…' : 'Load more'}
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}

export default Notifications
